import { Component, Input, OnChanges, ViewEncapsulation } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

interface Panel {
   id: string;
   title: string;
   content: SafeHtml;
}

@Component({
    selector: 'app-faqs',
    templateUrl: './faqs.component.html',
    styleUrls: ['./faqs.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class FaqsComponent implements OnChanges {



   @Input() name: string = '';
   @Input() ticker: string = '';
   @Input() halalStatus: string = '';
   @Input() monthYear: string = '';
   @Input() type: 'stock' | 'etf' = 'stock';

   panels: Panel[] = [];

   constructor(private sanitizer: DomSanitizer) {}

   ngOnChanges() {
      this.generatePanels();
   }

   generatePanels() {
      let rawPanels: { id: string; title: string; content: string }[] = [];
      if (this.type === 'stock') {
         rawPanels = [
            {
               id: 'panel-1',
               title: `Is ${this.name} halal to invest in?`,
               content: `According to Musaffa’s Shariah screening methodology, <b>${this.name} (${this.ticker})</b> is currently classified as <b>${this.halalStatus}</b> as of <b>${this.monthYear}</b>. The classification is based on an evaluation of the company’s business activities and financial ratios to determine whether it meets Islamic investment guidelines.`
            },
            {
               id: 'panel-2',
               title: `Is ${this.name} a Shariah-compliant stock?`,
               content: `A stock is Shariah-compliant if the company operates in permissible business activities and meets Islamic financial screening thresholds. Musaffa analyzes <b>${this.name}</b> using these criteria to determine its compliance status.`
            },
            {
               id: 'panel-3',
               title: `Can Muslims buy shares of ${this.name}?`,
               content: `Muslim investors may consider investing in <b>${this.name}</b> if it meets Shariah compliance standards. Investors typically review the company’s business activities, debt levels, and non-permissible income before making a decision.`
            },
            {
               id: 'panel-4',
               title: `Why is ${this.name} classified as ${this.halalStatus}?`,
               content: `Musaffa determines the Shariah status of <b>${this.name}</b> by analyzing whether the company’s core business operations are permissible and whether its financial ratios fall within accepted Islamic thresholds.`
            },
            {
               id: 'panel-5',
               title: `How often is the Shariah compliance for ${this.name} updated?`,
               content: `The Shariah compliance status of <b>${this.name}</b> may be updated periodically when new financial statements, earnings reports, or business activity information become available.`
            },
            {
               id: 'panel-6',
               title: `Can a halal stock like ${this.name} become non-compliant?`,
               content: `Yes. A stock that is currently Shariah-compliant may become non-compliant if the company’s financial ratios exceed Shariah thresholds or its business activities change.`
            },
            {
               id: 'panel-7',
               title: `Where can I check the Shariah status of ${this.name}?`,
               content: `You can check the latest Shariah compliance status of <b>${this.name}</b> on Musaffa’s stock page, where screening results and key financial indicators are updated to help Muslim investors make informed decisions.`
            }
         ];
      } else {
         rawPanels = [
            {
               id: 'panel-1',
               title: `Is ${this.name} halal to invest in?`,
               content: `According to Musaffa’s Shariah screening methodology, <b>${this.name} (${this.ticker})</b> is currently classified as <b>${this.halalStatus}</b> as of <b>${this.monthYear}</b>. The classification is based on an evaluation of the ETF’s underlying holdings and financial exposure to determine whether it meets Islamic investment guidelines.`
            },
            {
               id: 'panel-2',
               title: `Is ${this.name} a Shariah-compliant ETF?`,
               content: `An ETF is considered Shariah-compliant if its underlying assets consist primarily of companies that operate in permissible business activities and meet Islamic financial screening thresholds. Musaffa analyzes <b>${this.name}</b> using these criteria to determine its compliance status.`
            },
            {
               id: 'panel-3',
               title: `How often is the Shariah compliance for ${this.name} updated?`,
               content: `The Shariah compliance status of <b>${this.name}</b> may be updated periodically when new portfolio disclosures, financial statements of underlying companies, or other relevant information becomes available.`
            },
            {
               id: 'panel-4',
               title: `Can a halal ETF like ${this.name} become non-compliant?`,
               content: `Yes. An ETF that is currently Shariah-compliant may become non-compliant if its underlying holdings change or if the financial ratios of the companies within the fund exceed Shariah thresholds.`
            },
            {
               id: 'panel-5',
               title: `Where can I check the Shariah status of ${this.name}?`,
               content: `You can check the latest Shariah compliance status of <b>${this.name}</b> on Musaffa’s ETF page, where screening results and key indicators are updated to help Muslim investors make informed decisions.`
            }
         ];
      }

      this.panels = rawPanels.map(panel => ({
         ...panel,
         content: this.sanitizer.bypassSecurityTrustHtml(panel.content)
      }));
   }

   activeIds: string[] = ['panel-1']; // Track open panels

   togglePanel(id: string) {
      this.activeIds = this.activeIds[0] === id ? [] : [id];
   }

   isOpen(id: string): boolean {
      return this.activeIds.includes(id);
   }

}
